// Dígitos de WhatsApp de un teléfono guardado, con la misma regla que el back (helper/phone/canonicalPhone.js):
// "<código de país><número nacional>", y los celulares argentinos llevan el 9 de móvil ("5493813844245").
// Es el formato que acepta la ruta /mensaje de Zoco Chat. Sin país se asume Argentina; tolera "0381 15 555-1234",
// "+54 3813844245" (sin el 9) y dígitos pelados. Lo que no se reconoce vuelve como dígitos sin tocar.

const PAISES = [
	{ code: "54", lengths: [10] },
	{ code: "591", lengths: [8] },
	{ code: "56", lengths: [9] },
	{ code: "57", lengths: [10] },
	{ code: "598", lengths: [8, 9] },
	{ code: "595", lengths: [9] },
	{ code: "51", lengths: [9] },
	{ code: "55", lengths: [10, 11] },
].sort((a, b) => b.code.length - a.code.length);

// Parte nacional argentina (sin el 54) -> 10 dígitos sin el 9, o null.
const nacionalArgentino = (rest) => {
	let d = rest;
	if (d.startsWith("0")) d = d.slice(1);
	if (d.startsWith("9")) d = d.slice(1);
	if (/^[123]\d{9}$/.test(d)) return d;
	if (/^[123]\d{11}$/.test(d)) {
		// area + "15" + abonado: el 11 es el único código de área de 2 dígitos.
		for (const area of d.startsWith("11") ? [2] : [3, 4]) {
			if (d.slice(area, area + 2) === "15") return d.slice(0, area) + d.slice(area + 2);
		}
	}
	return null;
};

const nacional = (pais, rest) => {
	if (pais.code === "54") {
		const n = nacionalArgentino(rest);
		return n ? `9${n}` : null;
	}
	if (rest.startsWith("0") && pais.lengths.includes(rest.length - 1)) return rest.slice(1);
	return pais.lengths.includes(rest.length) ? rest : null;
};

export const digitosDeWhatsapp = (phone) => {
	const raw = String(phone ?? "").trim();
	const pelados = raw.replace(/\D/g, "");
	if (!pelados || /[a-z]/i.test(raw)) return pelados;

	let digits = pelados;
	let internacional = raw.startsWith("+");
	if (!internacional && digits.startsWith("00")) {
		internacional = true;
		digits = digits.slice(2);
	}
	if (digits.length < 7 || digits.length > 15) return pelados;

	if (internacional || !digits.startsWith("0")) {
		const pais = PAISES.find((p) => digits.startsWith(p.code));
		if (pais) {
			const n = nacional(pais, digits.slice(pais.code.length));
			if (n) return pais.code + n;
		}
		if (internacional) return pelados;
	}
	const ar = nacionalArgentino(digits);
	return ar ? `549${ar}` : pelados;
};

/** Teléfono de un formulario de reserva tal como se guarda: "<prefijo> <nacional>" con el prefijo del país elegido
 *  ("+591 71234567"). Si el cliente ya escribió el prefijo, no se duplica. Vacío queda vacío. */
export const telefonoConPrefijo = (phone, prefix = "+54") => {
	const nacionalEscrito = String(phone || "").trim();
	if (!nacionalEscrito) return "";
	const prefijo = prefix || "+54";
	const digitos = prefijo.replace(/\D/g, "");
	const sinPrefijo = nacionalEscrito.replace(new RegExp("^\\+?" + digitos + "\\s*"), "");
	return `${prefijo} ${sinPrefijo}`;
};

/** Teléfono guardado, legible y igual para datos nuevos y viejos: "+54 9 3813844245" para un celular argentino,
 *  "+<país> <número>" para los demás países soportados. Lo que no se reconoce vuelve tal cual, sin espacios de más. */
export const telefonoParaMostrar = (phone) => {
	const raw = String(phone ?? "").trim();
	if (!raw) return "";
	const d = digitosDeWhatsapp(raw);
	if (/^549[123]\d{9}$/.test(d)) return `+54 9 ${d.slice(3)}`;
	const pais = PAISES.find((p) => p.code !== "54" && d.startsWith(p.code) && p.lengths.includes(d.length - p.code.length));
	return pais ? `+${pais.code} ${d.slice(pais.code.length)}` : raw;
};
